import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import FormField from "../components/forms/FormField";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import SuccessMessage from "../components/common/SuccessMessage";
import Logo from "../components/common/Logo";
import { ROUTES, ERROR_MESSAGES, SUCCESS_MESSAGES } from "../utils/constants";
import {
  validatePassword,
  validatePasswordStrength,
} from "../utils/validation";

/**
 * ChangePassword Page Component
 *
 * Provides secure password change functionality with:
 * - Password change form with validation
 * - Password strength requirements
 * - Success/error feedback
 * - Redirect after successful change
 *
 * Requirements: 7.3, 7.4, 7.5
 */
const ChangePassword = () => {
  const navigate = useNavigate();
  const { changePassword, loading, error, clearError, isAuthenticated, user } =
    useAuth();

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({
    score: 0,
    feedback: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [changeSuccess, setChangeSuccess] = useState(false);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate(ROUTES.ADMIN_LOGIN, { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Update password strength when new password changes
  useEffect(() => {
    if (formData.newPassword) {
      setPasswordStrength(validatePasswordStrength(formData.newPassword));
    } else {
      setPasswordStrength({ score: 0, feedback: [] });
    }
  }, [formData.newPassword]);

  /**
   * Handle form field changes
   * @param {Event} e - Input change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }

    // Clear success message when user starts typing
    if (changeSuccess) {
      setChangeSuccess(false);
    }
  };

  /**
   * Validate form fields
   * @returns {boolean} True if form is valid
   */
  const validateForm = () => {
    const errors = {};

    // Current password validation
    if (!formData.currentPassword.trim()) {
      errors.currentPassword = ERROR_MESSAGES.REQUIRED_FIELD;
    }

    // New password validation
    if (!formData.newPassword.trim()) {
      errors.newPassword = ERROR_MESSAGES.REQUIRED_FIELD;
    } else if (!validatePassword(formData.newPassword)) {
      errors.newPassword = ERROR_MESSAGES.WEAK_PASSWORD;
    } else if (formData.newPassword === formData.currentPassword) {
      errors.newPassword =
        "New password must be different from current password";
    }

    // Confirm password validation
    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = ERROR_MESSAGES.REQUIRED_FIELD;
    } else if (formData.confirmPassword !== formData.newPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await changePassword(formData.currentPassword, formData.newPassword);
      setChangeSuccess(true);

      // Clear form data
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      // Redirect to dashboard after a short delay
      setTimeout(() => {
        navigate(ROUTES.ADMIN_DASHBOARD, { replace: true });
      }, 2000);
    } catch (err) {
      // Error is handled by the AuthContext and displayed via the error state
      console.error("Password change error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Check if form can be submitted
   * @returns {boolean} True if form can be submitted
   */
  const canSubmit = () => {
    return (
      formData.currentPassword.trim() &&
      formData.newPassword.trim() &&
      formData.confirmPassword.trim() &&
      passwordStrength.score === 5 &&
      !isSubmitting &&
      !loading
    );
  };

  /**
   * Get password strength indicator class
   * @returns {string} CSS class for password strength
   */
  const getPasswordStrengthClass = () => {
    if (passwordStrength.score === 0) return "";
    if (passwordStrength.score <= 2) return "weak";
    if (passwordStrength.score <= 4) return "medium";
    return "strong";
  };

  /**
   * Get password strength text
   * @returns {string} Password strength description
   */
  const getPasswordStrengthText = () => {
    if (passwordStrength.score === 0) return "";
    if (passwordStrength.score <= 2) return "Weak";
    if (passwordStrength.score <= 4) return "Medium";
    return "Strong";
  };

  return (
    <div className="change-password-page login-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <Logo size="medium" className="page-logo" />
          <h1 className="page-title">Change Password</h1>
          <p className="page-description">
            Update your password to maintain account security. Your new password
            must meet all security requirements.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        <div className="container">
          <div className="change-password-form-container">
            <div className="change-password-card">
              <div className="card-header">
                <h2 className="card-title">Update Password</h2>
                {user && (
                  <p className="card-subtitle">
                    Logged in as: <strong>{user.email}</strong>
                  </p>
                )}
              </div>

              <div className="card-body">
                {/* Success Message */}
                {changeSuccess && (
                  <div className="form-message success-section">
                    <SuccessMessage
                      message={SUCCESS_MESSAGES.PASSWORD_CHANGED}
                      className="password-change-success"
                    />
                    <p className="redirect-notice">
                      Redirecting to dashboard...
                    </p>
                  </div>
                )}

                {/* Authentication Error */}
                {error && (
                  <div className="form-message error-section">
                    <ErrorMessage message={error} className="auth-error" />
                  </div>
                )}

                {/* Change Password Form */}
                <form
                  onSubmit={handleSubmit}
                  className="change-password-form"
                  noValidate
                >
                  {/* Current Password Field */}
                  <FormField
                    label="Current Password"
                    name="currentPassword"
                    type="password"
                    value={formData.currentPassword}
                    onChange={handleInputChange}
                    error={formErrors.currentPassword}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your current password"
                    disabled={isSubmitting || loading || changeSuccess}
                  />

                  {/* New Password Field */}
                  <div className="password-field-container">
                    <FormField
                      label="New Password"
                      name="newPassword"
                      type="password"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      error={formErrors.newPassword}
                      required
                      autoComplete="new-password"
                      placeholder="Enter your new password"
                      disabled={isSubmitting || loading || changeSuccess}
                    />

                    {/* Password Strength Indicator */}
                    {formData.newPassword && (
                      <div className="password-strength">
                        <div className="strength-bar">
                          <div
                            className={`strength-fill ${getPasswordStrengthClass()}`}
                            style={{
                              width: `${(passwordStrength.score / 5) * 100}%`,
                            }}
                          />
                        </div>
                        <div className="strength-info">
                          <span
                            className={`strength-text ${getPasswordStrengthClass()}`}
                          >
                            {getPasswordStrengthText()}
                          </span>
                          {passwordStrength.feedback.length > 0 && (
                            <ul className="strength-feedback">
                              {passwordStrength.feedback.map((item, index) => (
                                <li key={index} className="feedback-item">
                                  {item}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Confirm Password Field */}
                  <FormField
                    label="Confirm New Password"
                    name="confirmPassword"
                    type="password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    error={formErrors.confirmPassword}
                    required
                    autoComplete="new-password"
                    placeholder="Confirm your new password"
                    disabled={isSubmitting || loading || changeSuccess}
                  />

                  {/* Form Actions */}
                  <div className="form-actions">
                    <button
                      type="button"
                      className="cancel-button"
                      onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}
                      disabled={isSubmitting || loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className={`submit-button ${
                        canSubmit() ? "enabled" : "disabled"
                      }`}
                      disabled={!canSubmit()}
                    >
                      {isSubmitting || loading ? (
                        <>
                          <LoadingSpinner size="small" />
                          <span>Updating...</span>
                        </>
                      ) : (
                        "Update Password"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="password-requirements">
              <div className="requirements-card">
                <h3 className="requirements-title">Password Requirements</h3>
                <ul className="requirements-list">
                  <li className="requirement-item">
                    At least 8 characters long
                  </li>
                  <li className="requirement-item">
                    Contains uppercase letters (A-Z)
                  </li>
                  <li className="requirement-item">
                    Contains lowercase letters (a-z)
                  </li>
                  <li className="requirement-item">Contains numbers (0-9)</li>
                  <li className="requirement-item">
                    Contains special characters (!@#$%^&*(),.?":{}|&lt;&gt;)
                  </li>
                </ul>
              </div>

              <div className="security-tips">
                <h3 className="tips-title">Security Tips</h3>
                <ul className="tips-list">
                  <li>Use a unique password that you don't use elsewhere</li>
                  <li>Consider using a passphrase with mixed characters</li>
                  <li>Avoid using personal information in your password</li>
                  <li>Change your password regularly</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
